<script setup lang="ts">
import DOMPurify from "dompurify";
import { marked } from "marked";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { brandRef } from "../brand-state";
import { useToast } from "../composables/useToast";
import { addCodeBars, handleCodeBarClick } from "../utils/codeBlocks";
import { useConfirm } from "../composables/useConfirm";
import { NARROW_QUERY, useMediaQuery } from "../composables/useMediaQuery";
import { sourcePane } from "../stores/source-pane";
import type { ChatTurn, Hit } from "../types/chat";
import { copyAsExcel, copyAsWord, hasTable } from "../utils/copyExport";
import type { ChartDirective, ChartSpec } from "../utils/chartSpec";
import {
  buildSpecFromRows,
  parseChartDirective,
  validateChartSpec,
} from "../utils/chartSpec";
import { clearMessageFeedback, setMessageFeedback } from "../api/conversations";
import CitationPopup from "./CitationPopup.vue";
import RetrievalBlock from "./RetrievalBlock.vue";
import DocumentCard from "./DocumentCard.vue";
import ToolRoundTripCard from "./ToolRoundTripCard.vue";
import ChartBlock from "./ChartBlock.vue";
import MermaidBlock from "./MermaidBlock.vue";

// Marked: GFM (tables, task lists), preserve linebreaks so the LLM's
// soft-wrapped output looks the same as the source.
marked.setOptions({ gfm: true, breaks: true });

const props = defineProps<{
  turn: ChatTurn;
  /** Conversation the turn lives in — required for feedback API calls
   * (the endpoint scopes votes to a specific conversation_id so cross-
   * conversation message-id collisions can't happen). Optional because
   * a few callers (export pane, transcript dialog) reuse the bubble
   * without a live conversation context. */
  conversationId?: number;
  /** For dev-auth mode — forwarded to the feedback API call. */
  devUser?: string;
}>();
const emit = defineEmits<{
  (e: "retry", message: string): void;
  /** Task #294 — emitted after a vote / vote-clear / comment-save so
   * the parent can update its in-memory turn list with the new
   * feedback state without re-fetching the conversation. */
  (
    e: "feedback-changed",
    payload: {
      turnId: string;
      feedback: {
        kind: "up" | "down";
        comment: string | null;
        updated_at?: string;
      } | null;
    },
  ): void;
}>();

const toast = useToast();
const { confirm } = useConfirm();

/** Lowercased product name for the header ("you" / "doit ai"). The
 * brand.productName is the display string; we lowercase it to match
 * the typographic register of the other header text. */
const assistantLabel = computed(() =>
  (brandRef.value.productName || "assistant").toLowerCase(),
);

/** Workflow slug → display label. The backend ships slugs in
 * kebab-case (matches the .txt filename); the badge wants
 * Title Case. Acronyms (FDA, CR, V-Model) keep their casing. */
const ACRONYMS: Record<string, string> = {
  fda: "FDA",
  cr: "CR",
  "v-model": "V-Model",
};
function humaniseWorkflow(slug: string): string {
  return slug
    .split("-")
    .map(
      (part) => ACRONYMS[part] ?? part.charAt(0).toUpperCase() + part.slice(1),
    )
    .join(" ");
}

/** Copy-the-answer action. Shows up on assistant turns once the
 * stream has settled (no point before the content is final). Strips
 * the `[N]` citation refs so what's on the clipboard reads like
 * normal prose, not "see [3] for the answer [5]". */
const canCopy = computed(
  () =>
    props.turn.role === "assistant" &&
    !!props.turn.content &&
    !props.turn.streaming,
);

/** Task #294 — feedback (thumbs-up / thumbs-down + optional comment).
 * Vote handlers POST to the backend feedback endpoint; the parent
 * holds the persisted state via @feedback-changed so a reopened
 * conversation rehydrates votes. `commentDraft` is local while the
 * textarea is open; `Save` pushes it through `setMessageFeedback`. */
const commentOpen = ref(false);
const commentDraft = ref(props.turn.feedback?.comment ?? "");
const savingComment = ref(false);

async function onVote(kind: "up" | "down"): Promise<void> {
  if (!props.turn.messageId || !props.conversationId) return;
  // Toggling the active vote clears it — keeps the UX from forcing
  // operators into a second click to "un-vote" when they misclicked.
  if (props.turn.feedback?.kind === kind) {
    await onClearVote();
    return;
  }
  const persisted = await setMessageFeedback(
    props.conversationId,
    props.turn.messageId,
    { kind, comment: props.turn.feedback?.comment ?? null },
    props.devUser,
  );
  if (!persisted) {
    toast.error("Couldn't save vote", "try again in a moment");
    return;
  }
  emit("feedback-changed", { turnId: props.turn.id, feedback: persisted });
}

async function onClearVote(): Promise<void> {
  if (!props.turn.messageId || !props.conversationId) return;
  const ok = await clearMessageFeedback(
    props.conversationId,
    props.turn.messageId,
    props.devUser,
  );
  if (!ok) {
    toast.error("Couldn't clear vote", "try again in a moment");
    return;
  }
  commentOpen.value = false;
  commentDraft.value = "";
  emit("feedback-changed", { turnId: props.turn.id, feedback: null });
}

async function onSaveComment(): Promise<void> {
  if (!props.turn.messageId || !props.conversationId || !props.turn.feedback) {
    return;
  }
  savingComment.value = true;
  try {
    const persisted = await setMessageFeedback(
      props.conversationId,
      props.turn.messageId,
      {
        kind: props.turn.feedback.kind,
        comment: commentDraft.value.trim() || null,
      },
      props.devUser,
    );
    if (!persisted) {
      toast.error("Couldn't save note", "try again in a moment");
      return;
    }
    commentOpen.value = false;
    emit("feedback-changed", { turnId: props.turn.id, feedback: persisted });
  } finally {
    savingComment.value = false;
  }
}

async function copyAnswer(): Promise<void> {
  const stripped = (props.turn.content ?? "").replace(/\[\d+\]/g, "").trim();
  if (!stripped) return;
  try {
    await navigator.clipboard.writeText(stripped);
    toast.success("Answer copied to clipboard");
  } catch {
    toast.error("Could not copy", "clipboard access denied");
  }
}

/** DOM ref to the rendered markdown so the Office-paste helpers can
 * grab the exact HTML the user sees (tables, lists, headings, cite
 * chips — all preserved structurally, then stripped of UI-only chrome
 * inside copyExport.ts before publishing to the clipboard). */
const contentEl = ref<HTMLElement | null>(null);

/** Whether the rendered answer has a `<table>` element. Drives the
 * "Copy for Excel" button visibility — no table → no Excel button
 * (the Word button still works for prose). Recomputed on every
 * render so streaming answers gain the button as soon as a table
 * lands in the markdown. */
const showExcelCopy = computed<boolean>(() => {
  if (!canCopy.value) return false;
  const el = contentEl.value;
  return el ? hasTable(el) : false;
});

async function copyForWord(): Promise<void> {
  const el = contentEl.value;
  if (!el) return;
  const ok = await copyAsWord(el);
  if (ok) {
    toast.success("Copied — paste into Word");
  } else {
    toast.error("Could not copy", "clipboard write blocked");
  }
}

async function copyForExcel(): Promise<void> {
  const el = contentEl.value;
  if (!el) return;
  const ok = await copyAsExcel(el);
  if (ok) {
    toast.success("Copied — paste into Excel");
  } else {
    toast.error("Could not copy", "clipboard write blocked");
  }
}

/** Share-via-email (ADR 0025 Gap 6). Opens the OS mail client with a
 * pre-filled subject + plain-text body containing the answer and a
 * "Sources:" appendix listing the cited hits. mailto only supports plain
 * text, but the URLs are clickable in every modern mail client. Body is
 * truncated to ~1500 chars so the mailto URL stays under the typical
 * 2000-char limit; if it overflows we fall back to copying the formatted
 * HTML to the clipboard so the user can paste it into a fresh compose. */
async function shareAnswer(): Promise<void> {
  const raw = (props.turn.content ?? "").trim();
  if (!raw) return;

  const hits = props.turn.retrieval?.hits ?? [];
  const subject = brandRef.value.productName
    ? `Answer from ${brandRef.value.productName}`
    : "Answer from Choir";

  // Plain-text body — strip markdown emphasis so it doesn't read as
  // "**bold**" in the email; keep [N] cite markers because the sources
  // list below resolves them.
  const plain = raw
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .trim();
  const truncated =
    plain.length > 1500
      ? plain.slice(0, 1500).trimEnd() +
        "\n\n[…truncated; see full answer in the original chat]"
      : plain;

  const sourceLines = hits
    .filter((h) => (h.url || "").trim().startsWith("http"))
    .slice(0, 10)
    .map((h) => `  [${h.n}] ${h.title || "(untitled)"} — ${h.url}`)
    .join("\n");
  const sourcesBlock = sourceLines ? `\n\nSources:\n${sourceLines}` : "";

  const body = `${truncated}${sourcesBlock}\n\n— shared from ${brandRef.value.productName || "Choir"}`;
  const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  // Browsers refuse URLs over ~2000 chars (esp. on Windows), so a full
  // answer can never travel inside the mailto URL. Copy the formatted
  // HTML to the clipboard and open a blank compose — but tell the user
  // via a BLOCKING dialog first: the browser switches to the mail
  // client immediately, so a toast is gone before anyone reads it and
  // the blank body reads as "share failed" (reported 2026-07-30).
  if (mailto.length > 1900) {
    const el = contentEl.value;
    const copied = el ? await copyAsWord(el) : false;
    if (copied) {
      const proceed = await confirm({
        title: "Answer copied — paste it into the email",
        message:
          "The answer is too long to pre-fill an email directly, so it " +
          "has been copied to your clipboard with formatting. A blank " +
          "email will open next — paste with Ctrl+V (Cmd+V on Mac).",
        confirmLabel: "Open email",
        cancelLabel: "Cancel",
      });
      if (proceed) {
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}`;
      }
      return;
    }
    toast.error(
      "Answer too long to share via mailto",
      "use Copy for Word and paste into a new email",
    );
    return;
  }

  window.location.href = mailto;
}

// ── Action bar ────────────────────────────────────────────────────────────
// Six actions side by side is wider than a phone screen, so on a narrow
// viewport all but Copy collapse into a `···` menu. Declaring them once and
// splitting the list is what keeps the phone and desktop layouts honest —
// two hand-maintained blocks of markup drift the moment an action is added.

type BubbleAction = {
  key: string;
  label: string;
  title: string;
  ariaLabel: string;
  run: () => void | Promise<void>;
};

const isNarrow = useMediaQuery(NARROW_QUERY);

const allActions = computed<BubbleAction[]>(() => {
  const out: BubbleAction[] = [
    {
      key: "copy",
      label: "Copy",
      title: "Copy answer to clipboard (plain text)",
      ariaLabel: "Copy answer to clipboard",
      run: copyAnswer,
    },
    {
      key: "word",
      label: "Copy for Word",
      title:
        "Copy as Word-pasteable HTML (preserves headings, lists, tables)",
      ariaLabel: "Copy for Word",
      run: copyForWord,
    },
  ];
  if (showExcelCopy.value) {
    out.push({
      key: "excel",
      label: "Copy for Excel",
      title: "Copy the first table for Excel paste",
      ariaLabel: "Copy for Excel",
      run: copyForExcel,
    });
  }
  // DOCX / HTML download buttons were here. They exported the WHOLE answer
  // into a blank document; `create_document` supersedes them — the assistant
  // builds the document the user actually asked for, on Danoffice IT
  // letterhead, and it arrives as its own download card. Keeping both would
  // offer two paths to a Word file that produce visibly different documents,
  // and the worse one was the more prominent.
  //
  // The /export endpoint and its `downloadMessageExport` client stay in
  // place -- ADR 0044 defines them and they are still the way to pull a past
  // message out without asking the assistant to rebuild it -- but nothing in
  // the chat UI calls them any more.
  out.push({
    key: "share",
    label: "Share",
    title:
      "Share this answer via email — pre-fills your mail client with the " +
      "answer and the cited sources",
    ariaLabel: "Share answer via email",
    run: shareAnswer,
  });
  return out;
});

/** Copy is the one people reach for, so it stays reachable without a tap
 *  to open anything. Everything else moves into the menu on a phone. */
const inlineActions = computed(() =>
  isNarrow.value ? allActions.value.slice(0, 1) : allActions.value,
);
const menuActions = computed(() =>
  isNarrow.value ? allActions.value.slice(1) : [],
);

/** Successful document round-trips on this turn: `create_document` (a new
 *  file in our template) and `fill_uploaded_template` (the customer's own
 *  file, filled in) both end in a download.
 *
 * Filtered on `ok` as well as name: a failed call still produces a
 * round-trip, and rendering a download button for a document that was never
 * built is worse than rendering nothing. The tool trace still shows the
 * failure for anyone who opens it. */
const documentTrips = computed(() =>
  (props.turn.tools ?? []).filter(
    (t) =>
      (t.name === "create_document" ||
        t.name === "create_service_pitch" ||
        t.name === "fill_uploaded_template") &&
      t.result?.ok &&
      t.result.payload?.ok,
  ),
);

const actionMenu = ref<HTMLDetailsElement | null>(null);

/** Run the action and close the menu — a `<details>` left open would
 *  otherwise cover the answer it was invoked from. */
async function runFromMenu(a: BubbleAction): Promise<void> {
  if (actionMenu.value) actionMenu.value.open = false;
  await a.run();
}

/** Render the FULL assistant content as one markdown document with
 * `[N]` citation refs pre-embedded as `<button>` placeholders. Marked
 * passes the inline HTML through unchanged, so the buttons land in
 * the right inline position (between sentences, inside list items,
 * inside table cells) instead of breaking paragraph flow.
 *
 * The buttons are NOT Vue components — event handlers attach via
 * delegation on the parent `.bubble__content` div (see
 * `onContentClick` / `onContentMouseover` below).
 *
 * Non-matching `[N]` (number outside the hit range) stays as plain
 * text so the user still sees it. */
function renderMarkdown(content: string): string {
  if (!content) return "";
  const hits = props.turn.retrieval?.hits ?? [];
  // Replace `[N]` BEFORE markdown parsing — marked's tokenizer
  // treats `<button>` as inline HTML and preserves it as-is. Use a
  // function so we can look up the hit per match and skip refs that
  // don't resolve to a real hit (leaves text intact).
  const withCites = content.replace(/\[(\d+)\]/g, (full, nStr) => {
    const n = Number(nStr);
    const hit = hits.find((h) => h.n === n);
    if (!hit) return full;
    // Tooltip includes section_title when present so hits from a
    // single-document index (e.g. GAMP - AI) still disambiguate by
    // section. Truncated to 80 chars so very long section names
    // don't overflow the OS tooltip layout.
    const sectionHint = hit.section_title
      ? ` — ${truncate(hit.section_title, 80)}`
      : "";
    const tip = `Source ${n}: ${hit.title || "(untitled)"}${sectionHint}`;
    // Profile-kind hits from `find_profiles` get a wider chip showing
    // the consultant name inline (e.g. `[2 · Alice Bjornsson]`) so the
    // booker sees who's being referenced without clicking. Same
    // data-attrs as a regular cite — same click handler, same preview
    // pane flow.
    const isProfile = hit.kind === "profile";
    const label =
      isProfile && hit.title
        ? `[${n} · ${escapeHtml(truncate(hit.title, 40))}]`
        : `[${n}]`;
    const extraClass = isProfile ? " bubble__cite--profile" : "";
    return (
      `<button type="button" class="bubble__cite${extraClass}" ` +
      `data-cite-n="${n}" data-cite-id="${escapeAttr(hit.id)}" ` +
      `title="${escapeAttr(tip)}"` +
      `>${label}</button>`
    );
  });
  const html = marked.parse(withCites, { async: false }) as string;
  // Code bars are added AFTER sanitising: they are our own static markup
  // (plus an escaped language name), and DOMPurify would strip their
  // data attributes otherwise.
  return addCodeBars(
    DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
      ADD_ATTR: ["data-cite-n", "data-cite-id"],
    }),
  );
}

/** One rendered piece of the assistant answer. `md` segments carry the
 * sanitised HTML (with citation chips); `chart` / `mermaid` segments are
 * rendered by their dedicated components. Splitting lets a ```chart or
 * ```mermaid fence become an interactive figure inline, while the prose
 * around it keeps the existing markdown + citation pipeline. */
type Segment =
  | { kind: "md"; html: string }
  | { kind: "chart"; spec: ChartSpec; key: string }
  | { kind: "mermaid"; src: string; seed: number };

// Closed ```chart / ```mermaid fences only — a half-streamed (unclosed)
// fence won't match, so it stays in the trailing markdown and renders as a
// normal code block until the closing ``` arrives. The fence body is
// non-greedy so consecutive blocks don't merge.
const FENCE_RE = /```(chart|mermaid)[ \t]*\r?\n([\s\S]*?)```/g;

/** Small stable hash of the turn id → unique-ish numeric base for mermaid
 * element ids (mermaid.render needs a DOM-unique id; two bubbles on screen
 * must not collide). */
function seedFromId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h);
}

const segments = computed<Segment[]>(() => {
  const content = props.turn.content ?? "";
  if (!content) return [];
  const out: Segment[] = [];
  const base = seedFromId(props.turn.id);
  let cursor = 0;
  let n = 0;
  const flushMd = (text: string) => {
    if (text.trim()) out.push({ kind: "md", html: renderMarkdown(text) });
  };
  // Re-create the regex per evaluation (it carries lastIndex state).
  const re = new RegExp(FENCE_RE.source, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    flushMd(content.slice(cursor, m.index));
    cursor = m.index + m[0].length;
    const lang = m[1];
    const body = m[2];
    if (lang === "chart") {
      // Untrusted JSON from the model — parse defensively. Two shapes:
      //   1. A run_sql *directive* (`{"source":"run_sql",...}`, no data): the
      //      model opted in to charting; we fill the numbers from the real
      //      run_sql tool result so it can't hallucinate bar heights.
      //   2. A full inline spec (labels + datasets) for numbers that aren't
      //      from run_sql (e.g. figures cited from retrieved text).
      // Anything malformed falls back to the raw fence as a code block.
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(body);
      } catch {
        parsed = null;
      }
      const directive = parseChartDirective(parsed);
      if (directive) {
        // Build from this turn's run_sql rows. If they haven't streamed in
        // yet (or aren't chartable) render nothing — never dump the directive
        // JSON at the reader. Recomputes once `turn.tools` arrives.
        const spec = chartFromRunSql(directive);
        if (spec) out.push({ kind: "chart", spec, key: `${base}-${n}` });
      } else {
        const spec = validateChartSpec(parsed);
        if (spec) {
          out.push({ kind: "chart", spec, key: `${base}-${n}` });
        } else {
          flushMd("```\n" + body + "```");
        }
      }
    } else {
      out.push({ kind: "mermaid", src: body.trim(), seed: base + n });
    }
    n++;
  }
  flushMd(content.slice(cursor));
  return out;
});

/** Resolve a model-emitted run_sql chart directive against this turn's actual
 * run_sql tool result(s). The numbers are the real query output (zero
 * hallucination risk); the directive only chooses whether/how to chart. Uses
 * the first ok run_sql trip whose rows are chartable. Returns null when no
 * such rows exist yet — charting is now opt-in, so we never auto-draw a chart
 * the model didn't ask for (that was the old over-eager `autoCharts`). */
function chartFromRunSql(directive: ChartDirective): ChartSpec | null {
  const trips = props.turn.tools ?? [];
  for (const t of trips) {
    if (t.name !== "run_sql" || !t.result?.ok) continue;
    const rows = t.result.payload?.rows_preview;
    if (!Array.isArray(rows)) continue;
    const spec = buildSpecFromRows(rows as Array<Record<string, unknown>>, {
      type: directive.type,
      title: directive.title,
      maxRows: directive.maxRows,
    });
    if (spec) return spec;
  }
  return null;
}

function escapeAttr(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Escape for HTML text content (not attributes). The chip label
 * for profile-kind citations passes the consultant's title through
 * this so a name like "O'Brien & Sons <jr>" doesn't break the
 * v-html render. DOMPurify sanitises a second time on the way out,
 * so this is defence in depth rather than the only escape. */
function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;
}

/** Hover state for the popup: which `n` is hovered, plus its anchor rect.
 * Click pins the popup so mouse-out doesn't dismiss it; clicking again
 * (or another ref) unpins. */
const hoveredN = ref<number | null>(null);
const pinnedN = ref<number | null>(null);
const anchorRect = ref<DOMRect | null>(null);

function hidePopup() {
  if (pinnedN.value === null) hoveredN.value = null;
}

/** Event delegation on `.bubble__content` — citations are rendered
 * by v-html as `<button data-cite-n="N" data-cite-id="…">` so they
 * aren't Vue children; we wire interactivity at the parent level. */
function onContentClick(event: MouseEvent) {
  // Copy / download on a code block's bar (see utils/codeBlocks).
  void handleCodeBarClick(event, {
    ok: (m) => toast.success(m),
    fail: (m) => toast.error(m),
  });
  const target = (event.target as HTMLElement | null)?.closest(
    "button.bubble__cite",
  ) as HTMLElement | null;
  if (!target) return;
  event.preventDefault();
  const n = Number(target.dataset.citeN);
  if (Number.isFinite(n)) togglePin(event, n);
}

function onContentOver(event: MouseEvent) {
  const target = (event.target as HTMLElement | null)?.closest(
    "button.bubble__cite",
  ) as HTMLElement | null;
  if (!target) return;
  const n = Number(target.dataset.citeN);
  if (!Number.isFinite(n)) return;
  // Synthesise the showPopup signature: the function reads
  // currentTarget's rect, so we hand it the button.
  hoveredN.value = n;
  anchorRect.value = target.getBoundingClientRect();
}

function onContentOut(event: MouseEvent) {
  const target = (event.target as HTMLElement | null)?.closest(
    "button.bubble__cite",
  );
  if (!target) return;
  hidePopup();
}

function togglePin(event: MouseEvent, n: number) {
  // Click [N] → if the source is a real HTTP(S) URL (SharePoint doc,
  // public web page, blob link), open it in a new tab — that's the
  // shortest path for "show me the actual document". For non-URL
  // sources (chunks with `url=""` or `chunk_id://…` schemes), fall
  // back to the right-rail SourcePreviewPane + hover popup.
  //
  // Hold Shift / Cmd / Ctrl to FORCE the pane open even when a URL is
  // present — useful when the user wants to scan the chunk text first
  // before clicking through.
  const hit = props.turn.retrieval?.hits.find((h) => h.n === n);
  if (!hit) {
    hoveredN.value = null;
    pinnedN.value = null;
    return;
  }
  // SQL results and whole uploaded documents have no underlying chunk to
  // fetch — the hover popup already shows them; the pane would just error.
  if (hit.kind === "sql" || hit.kind === "upload") {
    pinnedN.value = pinnedN.value === n ? null : n;
    return;
  }
  const url = (hit.url || "").trim();
  const isHttp = url.startsWith("http://") || url.startsWith("https://");
  const forcePane = event.shiftKey || event.metaKey || event.ctrlKey;
  if (isHttp && !forcePane) {
    window.open(url, "_blank", "noopener,noreferrer");
    hoveredN.value = null;
    pinnedN.value = null;
    return;
  }
  // Either there's no URL, or the user asked to see the chunk text
  // first — pin into the right-rail pane.
  sourcePane.pin({
    chunkId: hit.id,
    turnId: props.turn.id,
    hit,
    answerText: props.turn.content,
  });
  pinnedN.value = null;
  hoveredN.value = null;
  scrollToSource(n);
}

/** Click anywhere outside the popup OR the [N] buttons → unpin. The
 * popup is teleported to body so we listen on the document and gate
 * with `.closest()`. Clicks on a `[N]` are handled by `togglePin`,
 * so we skip them here to avoid a double-toggle. */
function onDocumentClick(event: MouseEvent) {
  if (pinnedN.value === null) return;
  const target = event.target as HTMLElement | null;
  if (!target) return;
  if (target.closest(".bubble__popup-wrap")) return;
  if (target.closest(".bubble__cite")) return;
  pinnedN.value = null;
  hoveredN.value = null;
}

onMounted(() => document.addEventListener("click", onDocumentClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocumentClick));

/** Scroll the matching source row into view + flash it. Anchor IDs are
 * stamped by `RetrievalBlock` as `source-<turnId>-<n>` so multiple bubbles
 * in the same conversation don't collide. */
function scrollToSource(n: number) {
  const el = document.getElementById(`source-${props.turn.id}-${n}`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  el.classList.add("retrieval__hit--flash");
  setTimeout(() => el.classList.remove("retrieval__hit--flash"), 1500);
}

const activeHit = computed<Hit | null>(() => {
  const n = pinnedN.value ?? hoveredN.value;
  if (n == null) return null;
  return props.turn.retrieval?.hits.find((h) => h.n === n) ?? null;
});

const popupStyle = computed<Record<string, string>>(() => {
  const r = anchorRect.value;
  if (!r) return {} as Record<string, string>;
  // Position below + slightly right of the ref. Clamp to viewport so the
  // popup doesn't disappear off the right edge for refs near the end of
  // a long line.
  const top = window.scrollY + r.bottom + 6;
  const left = Math.max(
    8,
    Math.min(window.scrollX + r.left, window.innerWidth - 460),
  );
  return { top: `${top}px`, left: `${left}px` };
});
</script>

<template>
  <article
    class="bubble"
    :class="`bubble--${turn.role}`"
    :aria-busy="turn.streaming || undefined"
  >
    <header class="bubble__header">
      <span
        v-if="turn.role === 'assistant'"
        class="bubble__avatar"
        aria-hidden="true"
      ></span>
      <span class="bubble__who">
        {{ turn.role === "user" ? "you" : assistantLabel }}
      </span>
      <span
        v-if="turn.streaming"
        class="bubble__stream-indicator"
        aria-hidden="true"
      >
        ▍
      </span>
      <span
        v-if="turn.streaming"
        class="bubble__stream-label"
        aria-live="polite"
      >
        thinking…
      </span>
      <!-- Workflow badge — shows which per-assistant workflow(s) the
           backend matched for this turn. The slug is the file stem
           (e.g. `fda-credibility-plan`); humanise it for display. -->
      <span
        v-for="slug in turn.matchedWorkflows ?? []"
        :key="slug"
        class="bubble__workflow"
        :title="`Backend injected workflow content from ${slug}.txt into the system prompt for this turn`"
      >
        <span class="bubble__workflow-dot" aria-hidden="true">▸</span>
        {{ humaniseWorkflow(slug) }}
      </span>
      <!-- Six actions in one row is wider than a phone screen. On a narrow
           viewport only Copy stays inline and the rest move into a menu;
           on a wide one the menu is flattened back out and every action is
           a button again. The list is built once in script so the two
           layouts cannot drift apart. -->
      <span v-if="canCopy" class="bubble__office-actions">
        <button
          v-for="a in inlineActions"
          :key="a.key"
          type="button"
          class="bubble__copy"
          :title="a.title"
          :aria-label="a.ariaLabel"
          @click="a.run"
        >
          {{ a.label }}
        </button>
        <details
          v-if="menuActions.length"
          ref="actionMenu"
          class="bubble__more"
        >
          <summary
            class="bubble__copy bubble__more-toggle"
            title="More actions"
            aria-label="More actions for this answer"
          >
            ···
          </summary>
          <div class="bubble__more-list">
            <button
              v-for="a in menuActions"
              :key="a.key"
              type="button"
              class="bubble__more-item"
              :title="a.title"
              :aria-label="a.ariaLabel"
              @click="runFromMenu(a)"
            >
              {{ a.label }}
            </button>
          </div>
        </details>
      </span>
    </header>

    <div v-if="turn.content" class="bubble__content">
      <!-- User turns stay as pre-wrap plaintext (a user message
           doesn't need block-level markdown rendering). -->
      <template v-if="turn.role === 'user'">{{ turn.content }}</template>
      <!-- Assistant turns: the content is split into ordered segments —
           markdown prose (with citation `[N]` chips) plus any inline
           ```chart / ```mermaid figures. Event delegation on the wrapper
           wires citation interactivity for all the markdown segments. -->
      <div
        v-else
        ref="contentEl"
        class="bubble__markdown"
        @click="onContentClick"
        @mouseover="onContentOver"
        @mouseout="onContentOut"
      >
        <template v-for="(seg, i) in segments" :key="i">
          <!-- eslint-disable-next-line vue/no-v-html — sanitised in renderMarkdown -->
          <div v-if="seg.kind === 'md'" v-html="seg.html" />
          <ChartBlock v-else-if="seg.kind === 'chart'" :spec="seg.spec" />
          <MermaidBlock v-else :src="seg.src" :id-seed="seg.seed" />
        </template>
      </div>
    </div>

    <!-- ADR 0017 Phase 3 — tool round-trips + retrieval block live
         BELOW the answer text so the reader sees the synthesized
         answer first. The cards are collapsed by default; users
         expand only the trip they want to inspect. -->
    <!-- Documents the assistant produced. Deliberately ABOVE the tool
         round-trips and outside the collapsible: a file the user asked for
         is the outcome of the turn, not diagnostics about how it ran. -->
    <DocumentCard
      v-for="d in documentTrips"
      :key="d.id"
      :conversation-id="conversationId"
      :payload="d.result!.payload"
      :dev-user="devUser"
    />

    <div v-if="turn.tools && turn.tools.length" class="bubble__tools">
      <ToolRoundTripCard v-for="t in turn.tools" :key="t.id" :trip="t" />
    </div>

    <RetrievalBlock
      v-if="turn.retrieval && turn.role === 'assistant'"
      :retrieval="turn.retrieval"
      :turn-id="turn.id"
    />

    <!-- Task #294 — per-message feedback. Only on assistant turns
         that have already settled (no point voting on a half-streamed
         answer) AND that have a backend-assigned messageId (in-flight
         turns haven't been persisted yet — vote becomes possible the
         moment the SSE `done` frame arrives with the id). -->
    <div
      v-if="turn.role === 'assistant' && !turn.streaming && turn.messageId"
      class="bubble__feedback"
    >
      <button
        type="button"
        class="bubble__fb-btn"
        :class="{ 'bubble__fb-btn--active': turn.feedback?.kind === 'up' }"
        :aria-pressed="turn.feedback?.kind === 'up'"
        title="Helpful answer"
        @click="onVote('up')"
      >
        👍
      </button>
      <button
        type="button"
        class="bubble__fb-btn"
        :class="{
          'bubble__fb-btn--active-down': turn.feedback?.kind === 'down',
        }"
        :aria-pressed="turn.feedback?.kind === 'down'"
        title="Not helpful"
        @click="onVote('down')"
      >
        👎
      </button>
      <button
        v-if="turn.feedback"
        type="button"
        class="bubble__fb-clear"
        title="Clear vote"
        @click="onClearVote"
      >
        ×
      </button>
      <button
        v-if="turn.feedback"
        type="button"
        class="bubble__fb-toggle-comment"
        @click="commentOpen = !commentOpen"
      >
        {{
          turn.feedback.comment
            ? "Edit note"
            : commentOpen
              ? "Cancel"
              : "Add note"
        }}
      </button>
    </div>
    <div
      v-if="
        commentOpen &&
        turn.role === 'assistant' &&
        turn.messageId &&
        turn.feedback
      "
      class="bubble__feedback-comment"
    >
      <textarea
        v-model="commentDraft"
        class="bubble__fb-textarea"
        :placeholder="
          turn.feedback.kind === 'up'
            ? 'What worked?'
            : 'What was missing or wrong?'
        "
        rows="2"
        maxlength="4000"
      ></textarea>
      <div class="bubble__fb-comment-actions">
        <button
          type="button"
          class="bubble__fb-save"
          :disabled="savingComment"
          @click="onSaveComment"
        >
          {{ savingComment ? "Saving…" : "Save" }}
        </button>
      </div>
    </div>

    <div v-if="turn.error" class="bubble__error" role="alert">
      <span class="bubble__error-msg">{{ turn.error }}</span>
      <button
        v-if="turn.retryMessage"
        type="button"
        class="bubble__retry"
        @click="emit('retry', turn.retryMessage)"
      >
        Try again
      </button>
    </div>

    <!-- Teleport popup to body to escape ancestor `overflow: hidden`. -->
    <Teleport to="body">
      <div
        v-if="activeHit"
        class="bubble__popup-wrap"
        :style="popupStyle"
        @mouseenter="hoveredN = activeHit.n"
        @mouseleave="hidePopup"
      >
        <CitationPopup :hit="activeHit" />
      </div>
    </Teleport>
  </article>
</template>

<style scoped>
.bubble {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-lg);
  border: 1px solid color-mix(in srgb, var(--color-border) 60%, transparent);
  background: var(--color-surface);
  box-shadow: var(--shadow-xs);
  /* `backwards`, not `both` — see the note on `.rise-in` in effects.css:
     `both` holds the end keyframe forever and outranks normal rules, which
     silently disables any later transform/opacity on the bubble. Nothing
     depends on it here today; this keeps the trap from being re-sprung. */
  animation: rise-in var(--dur-3) var(--ease-out) backwards;
}
/* User bubbles are visibly shorter than assistant ones (one-line
   questions vs multi-paragraph answers); cap them tighter so they
   sit right-aligned without sprawling. */
.bubble--user {
  background: color-mix(in srgb, var(--color-accent) 8%, var(--color-surface));
  border-color: color-mix(in srgb, var(--color-accent) 14%, transparent);
  align-self: flex-end;
  max-width: min(60ch, 90%);
}
/* A small aurora-spark dot marks the assistant's identity in the header
   without a full avatar column. */
.bubble__avatar {
  width: 0.65rem;
  height: 0.65rem;
  border-radius: 50%;
  background:
    radial-gradient(120% 120% at 30% 25%, var(--aurora-4), transparent 60%),
    radial-gradient(120% 120% at 75% 80%, var(--aurora-3), transparent 60%),
    var(--aurora-1);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-accent) 12%, transparent);
  align-self: center;
}
/* Assistant bubbles fill the transcript's 74ch reading column. */
.bubble--assistant {
  align-self: stretch;
  width: 100%;
}
.bubble__header {
  font-size: 0.75rem;
  color: var(--color-muted);
  text-transform: lowercase;
  letter-spacing: 0.02em;
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
}
.bubble__who {
  font-weight: 500;
}
.bubble__stream-indicator {
  animation: blink 1s step-end infinite;
  color: var(--color-accent);
}
.bubble__content {
  word-wrap: break-word;
  line-height: 1.55;
}
/* Markdown elements rendered via v-html — keep the typography
   consistent with the rest of the chat surface, since these
   come from the LLM not from the design system. Tight margins
   so each bubble feels like one continuous thought, not an
   article. */
.bubble__content :deep(h1),
.bubble__content :deep(h2),
.bubble__content :deep(h3),
.bubble__content :deep(h4) {
  margin: 1.1em 0 0.4em;
  font-weight: 700;
  line-height: 1.3;
}
.bubble__content :deep(h1):first-child,
.bubble__content :deep(h2):first-child,
.bubble__content :deep(h3):first-child,
.bubble__content :deep(h4):first-child {
  margin-top: 0;
}
.bubble__content :deep(h1) {
  font-size: 1.4rem;
}
.bubble__content :deep(h2) {
  font-size: 1.2rem;
}
.bubble__content :deep(h3) {
  font-size: 1.05rem;
}
.bubble__content :deep(h4) {
  font-size: 0.95rem;
}
.bubble__content :deep(p) {
  margin: 0.5em 0;
}
.bubble__content :deep(p):last-child {
  margin-bottom: 0;
}
.bubble__content :deep(p):first-child {
  margin-top: 0;
}
.bubble__content :deep(ul),
.bubble__content :deep(ol) {
  margin: 0.5em 0;
  padding-left: 1.4em;
}
.bubble__content :deep(li) {
  margin: 0.2em 0;
}
.bubble__content :deep(li > ul),
.bubble__content :deep(li > ol) {
  margin: 0.2em 0;
}
.bubble__content :deep(strong) {
  font-weight: 700;
}
.bubble__content :deep(em) {
  font-style: italic;
}
.bubble__content :deep(code) {
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 0.88em;
  background: var(--color-surface-alt);
  padding: 0.1em 0.35em;
  border-radius: var(--radius-sm);
}
.bubble__content :deep(pre) {
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 0.75em 1em;
  margin: 0.6em 0;
  overflow-x: auto;
  font-size: 0.85em;
  line-height: 1.45;
}
/* Code block bar: language + Kopiér + Download (utils/codeBlocks). */
.bubble__content :deep(.codeblock) {
  margin: var(--space-2) 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
}
.bubble__content :deep(.codeblock pre) {
  margin: 0;
  border-radius: 0;
}
.bubble__content :deep(.codeblock__bar) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: 0.25rem 0.5rem;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  font-size: 0.78rem;
}
.bubble__content :deep(.codeblock__lang) {
  color: var(--color-ink-muted, #6b7280);
  text-transform: lowercase;
}
.bubble__content :deep(.codeblock__actions) {
  display: flex;
  gap: 0.35rem;
}
.bubble__content :deep(.codeblock__btn) {
  font: inherit;
  padding: 0.15rem 0.55rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm, 6px);
  background: transparent;
  color: var(--color-ink);
  cursor: pointer;
}
.bubble__content :deep(.codeblock__btn:hover) {
  border-color: var(--color-accent);
}
.bubble__content :deep(pre code) {
  background: transparent;
  padding: 0;
  font-size: 1em;
}
.bubble__content :deep(blockquote) {
  margin: 0.6em 0;
  padding: 0.4em 0.9em;
  border-left: 3px solid var(--color-accent);
  color: var(--color-muted);
  background: var(--color-surface-alt);
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
}
.bubble__content :deep(a) {
  color: var(--color-accent);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}
.bubble__content :deep(a:hover) {
  color: var(--color-accent-hover);
}
.bubble__content :deep(hr) {
  border: 0;
  border-top: 1px solid var(--color-border);
  margin: 1em 0;
}
.bubble__content :deep(table) {
  border-collapse: collapse;
  margin: 0.6em 0;
  font-size: 0.9em;
  /* Tables in regulatory content (risk matrix, V-Model) often have
     long cells — let them scroll horizontally if the bubble can't
     fit them. */
  display: block;
  overflow-x: auto;
  max-width: 100%;
}
.bubble__content :deep(th),
.bubble__content :deep(td) {
  border: 1px solid var(--color-border);
  padding: 0.4em 0.75em;
  text-align: left;
  vertical-align: top;
}
.bubble__content :deep(th) {
  background: var(--color-surface-alt);
  font-weight: 600;
}
/* GFM task list checkboxes — restyle so they don't look like
   raw HTML form controls. */
.bubble__content :deep(input[type="checkbox"]) {
  margin-right: 0.4em;
  vertical-align: middle;
}
/* Citations are v-html-injected buttons (no scoped data attr), so we
   need :deep() so Vue's scoped CSS still applies. Targeted at the
   `.bubble__markdown` wrapper to keep the rule specific to chat
   bubble content. */
.bubble__content :deep(button.bubble__cite) {
  display: inline-flex;
  align-items: center;
  padding: 0 0.3em;
  margin: 0 0.05em;
  /* Highlight, not accent. Citations are the thing a reader's eye lands on
     most often in an answer, and in navy-on-navy they read as plain bold
     text; the brand's secondary accent is what makes them look clickable. */
  background: color-mix(in srgb, var(--color-highlight) 12%, transparent);
  color: color-mix(in srgb, var(--color-highlight) 80%, var(--color-ink));
  border: 1px solid color-mix(in srgb, var(--color-highlight) 35%, transparent);
  border-radius: var(--radius-sm);
  font-size: 0.85em;
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;
  vertical-align: baseline;
  transition:
    background 120ms ease,
    transform 120ms ease;
  /* Reset the user-agent button defaults that would otherwise
     leak through (font-family inheritance + display: inline-block). */
  font-family: inherit;
}
.bubble__content :deep(button.bubble__cite:hover) {
  background: color-mix(in srgb, var(--color-highlight) 22%, transparent);
  transform: translateY(-1px);
}
.bubble__content :deep(button.bubble__cite:focus-visible) {
  outline: 2px solid var(--color-accent);
  outline-offset: 1px;
}
/* Profile-kind citation chips show the consultant name inline
 * (Booking Matcher) — slightly wider chip, ever-so-slightly different
 * tint so the user spots "this is a person, click for full profile"
 * at a glance. Same click handler / preview-pane flow as a regular
 * cite; only the visual treatment differs. */
.bubble__content :deep(button.bubble__cite--profile) {
  padding: 0 0.55em;
  font-weight: 600;
  letter-spacing: 0.01em;
  background: color-mix(in srgb, var(--color-accent) 9%, transparent);
  border-color: color-mix(in srgb, var(--color-accent) 45%, transparent);
}
.bubble__content :deep(button.bubble__cite--profile:hover) {
  background: color-mix(in srgb, var(--color-accent) 18%, transparent);
}
.bubble__feedback {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: var(--space-2);
  font-size: 0.85rem;
}
.bubble__fb-btn {
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 2px 8px;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1.2;
  opacity: 0.55;
  transition:
    opacity 0.1s,
    background 0.1s,
    border-color 0.1s;
}
.bubble__fb-btn:hover {
  opacity: 1;
  background: var(--color-surface-alt, rgba(0, 0, 0, 0.03));
}
.bubble__fb-btn--active {
  opacity: 1;
  background: color-mix(in srgb, #047857 18%, transparent);
  border-color: color-mix(in srgb, #047857 50%, transparent);
}
.bubble__fb-btn--active-down {
  opacity: 1;
  background: color-mix(in srgb, #b91c1c 14%, transparent);
  border-color: color-mix(in srgb, #b91c1c 45%, transparent);
}
.bubble__fb-clear {
  background: transparent;
  border: none;
  color: var(--color-muted);
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  padding: 0 0.3rem;
}
.bubble__fb-clear:hover {
  color: var(--color-ink);
}
.bubble__fb-toggle-comment {
  background: transparent;
  border: none;
  color: var(--color-muted);
  cursor: pointer;
  font-size: 0.8rem;
  text-decoration: underline;
  padding: 0;
}
.bubble__fb-toggle-comment:hover {
  color: var(--color-ink);
}
.bubble__feedback-comment {
  margin-top: var(--space-2);
}
.bubble__fb-textarea {
  width: 100%;
  font-family: inherit;
  font-size: 0.85rem;
  padding: 0.4rem 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: inherit;
  resize: vertical;
  box-sizing: border-box;
}
.bubble__fb-textarea:focus {
  outline: none;
  border-color: var(--color-accent);
}
.bubble__fb-comment-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.3rem;
}
.bubble__fb-save {
  background: var(--color-accent);
  color: white;
  border: none;
  border-radius: var(--radius-sm);
  padding: 0.3rem 0.8rem;
  font-size: 0.85rem;
  cursor: pointer;
}
.bubble__fb-save:disabled {
  opacity: 0.5;
  cursor: default;
}
.bubble__error {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--color-error);
  font-size: 0.85rem;
  background: color-mix(in srgb, var(--color-error) 8%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-error) 30%, transparent);
  border-radius: var(--radius-sm);
  padding: 0.5rem 0.75rem;
}
.bubble__error-msg {
  flex: 1;
}
.bubble__retry {
  background: transparent;
  border: 1px solid var(--color-error);
  color: var(--color-error);
  border-radius: var(--radius-sm);
  padding: 0.2rem 0.6rem;
  font-size: 0.8rem;
  cursor: pointer;
  font-weight: 600;
}
.bubble__retry:hover {
  background: var(--color-error);
  color: var(--color-surface);
}
.bubble__retry:focus-visible {
  outline: 2px solid var(--color-error);
  outline-offset: 2px;
}
.bubble__stream-label {
  font-size: 0.75rem;
  color: var(--color-muted);
  font-style: italic;
}
.bubble__workflow {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-accent) 14%, transparent);
  color: var(--color-accent);
  border: 1px solid color-mix(in srgb, var(--color-accent) 35%, transparent);
  text-transform: none;
  white-space: nowrap;
  cursor: help;
}
.bubble__workflow-dot {
  font-size: 0.65rem;
  opacity: 0.85;
}
.bubble__office-actions {
  margin-left: auto;
  display: inline-flex;
  gap: 0.35rem;
  align-items: center;
  /* Belt and braces with the `···` menu: even on a viewport just above the
     breakpoint, six buttons plus a long assistant name must wrap rather
     than push the bubble wider than the screen. */
  flex-wrap: wrap;
  justify-content: flex-end;
}

/* ── Overflow menu (phones) ─────────────────────────────────────────────
   `<details>` rather than a hand-rolled popover: Escape, click-to-close and
   keyboard focus come from the browser, and it degrades to an inline
   disclosure if the CSS never loads. */
.bubble__more {
  position: relative;
  display: inline-block;
}
.bubble__more-toggle {
  /* Inherits .bubble__copy for the border/size; this adds the affordances a
     <summary> does not get for free. */
  list-style: none;
  display: inline-flex;
  align-items: center;
  line-height: 1;
  user-select: none;
}
.bubble__more-toggle::-webkit-details-marker {
  display: none;
}
.bubble__more-list {
  position: absolute;
  right: 0;
  top: calc(100% + 0.25rem);
  z-index: 20;
  display: flex;
  flex-direction: column;
  min-width: 11rem;
  padding: 0.25rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-md);
}
.bubble__more-item {
  appearance: none;
  border: 0;
  background: none;
  color: var(--color-ink);
  text-align: left;
  /* Comfortable tap target — this menu exists for touch. */
  padding: 0.5rem 0.6rem;
  border-radius: var(--radius-sm);
  font-size: 0.85rem;
  cursor: pointer;
  white-space: nowrap;
}
.bubble__more-item:hover,
.bubble__more-item:focus-visible {
  background: var(--color-border);
}
.bubble__copy {
  background: none;
  border: 1px solid var(--color-border);
  color: var(--color-muted);
  padding: 0.15rem 0.5rem;
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  cursor: pointer;
  transition:
    color 120ms ease,
    border-color 120ms ease;
}
.bubble__office-actions > .bubble__copy {
  /* Inside the office-actions cluster the margin-auto from the legacy
     standalone button isn't wanted — the cluster handles the right-
     edge alignment via header's flex layout. */
  margin-left: 0;
}
.bubble__copy:hover {
  color: var(--color-ink);
  border-color: var(--color-ink);
}
.bubble__copy:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 1px;
}
.bubble__popup-wrap {
  position: absolute;
  z-index: 1000;
}
@keyframes blink {
  50% {
    opacity: 0;
  }
}
.bubble__tools {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
</style>
